import type { FormikErrors, FormikHelpers } from 'formik';
import { Form, FormikProvider, useFormik } from 'formik';
import { useEffect } from 'react';

import type { KeyPortRequestMessage } from '../../types/KeyPortRequestMessage';
import type { ValidationResponse } from '../../types/ValidationResponse';
import type { VesselMessage } from '../../types/VesselMessage';
import { Button } from '../cdl/Button/Button';
import { DrawerContentLayout } from '../cdl/Drawer/DrawerContentLayout';
import { useDrawer } from '../cdl/Drawer/useDrawer.hook';
import { FormikCustomerGroupSelect } from '../common/form-elements/formik/FormikCustomerGroupSelect';
import { FormikDebug } from '../common/form-elements/formik/FormikDebug';
import { translate } from '../common/helpers/translate.helper';
import { useRole } from '../common/hooks/useRole';
import { FormikPortDetailSelect } from '../port/portSelect/FormikPortDetailSelect';

import { FormVesselLimitationsSection } from './FormVesselLimitationsSection';
import { KeyPortVesselScope } from './types/KeyPortVesselScope';

const EMPTY_SERVER_ERRORS: ValidationResponse['errors'] = [];

interface KeyPortFormData {
    customerGroupId?: string;
    portId?: string;
    vesselLimitationsVessels?: VesselMessage[];
}

interface KeyPortFormValues {
    customerGroupId: string | null;
    portId: string;
    vesselScope: KeyPortVesselScope;
    vessels: VesselMessage[];
}

interface KeyPortFormProps {
    onSubmit: (args: { data: KeyPortRequestMessage }) => void | Promise<void>;
    data?: KeyPortFormData;
    errors?: ValidationResponse['errors'];
    update?: boolean;
}

export const KeyPortForm = ({
    onSubmit,
    data: keyPort = {},
    errors = EMPTY_SERVER_ERRORS,
    update = false,
}: KeyPortFormProps) => {
    const role = useRole();
    const { confirm } = useDrawer();

    const initialValues: KeyPortFormValues = {
        customerGroupId: keyPort.customerGroupId ?? null,
        portId: keyPort.portId ?? '',
        vesselScope: keyPort.vesselLimitationsVessels?.length ? KeyPortVesselScope.SELECTED : KeyPortVesselScope.ALL,
        vessels: keyPort.vesselLimitationsVessels ?? [],
    };

    const validate = (values: KeyPortFormValues) => {
        const validationErrors: FormikErrors<KeyPortFormValues> = {};

        if (!values.customerGroupId) {
            validationErrors.customerGroupId = translate('errormessage.empty');
        }

        if (!values.portId) {
            validationErrors.portId = translate('errormessage.empty');
        }

        return validationErrors;
    };

    const handleSubmit = async (values: KeyPortFormValues, { setFieldValue }: FormikHelpers<KeyPortFormValues>) => {
        if (!values.customerGroupId) {
            return;
        }

        let applyToAll = values.vesselScope === KeyPortVesselScope.ALL;
        if (!applyToAll && values.vessels.length === 0) {
            const confirmed = await confirm({
                title: translate('keyPorts.applyKeyPortToAllVessels'),
                description: translate('keyPorts.noVesselsSelectedApplyToAll'),
                confirmText: translate('keyPorts.applyToAllVessels'),
            });
            if (!confirmed) {
                return;
            }
            await setFieldValue('vesselScope', KeyPortVesselScope.ALL, false);
            applyToAll = true;
        }

        return onSubmit({
            data: {
                customerGroupId: values.customerGroupId,
                portId: values.portId,
                vesselLimitations: applyToAll ? [] : values.vessels.map((vessel) => vessel.id),
            },
        });
    };

    const formik = useFormik<KeyPortFormValues>({
        initialValues,
        validate,
        validateOnMount: true,
        onSubmit: handleSubmit,
    });
    const { values, isSubmitting, setFieldError, setFieldTouched } = formik;

    useEffect(() => {
        const duplicatePortError = errors.some((error) => error.code === 'DUPLICATE' && error.path === 'portId');

        if (duplicatePortError) {
            setFieldError('portId', translate('keyPorts.duplicateError'));
            setFieldTouched('portId', true, false);
        }
    }, [errors, setFieldError, setFieldTouched]);

    return (
        <FormikProvider value={formik}>
            <Form style={{ height: '100%', minHeight: 0 }}>
                <DrawerContentLayout
                    isBusy={isSubmitting}
                    hasUnsavedChanges={formik.dirty}
                    primaryAction={
                        <Button type="submit" emphasis="high" isLoading={isSubmitting}>
                            {translate(update ? 'keyPorts.update' : 'keyPorts.addKeyPort')}
                        </Button>
                    }
                >
                    {!update && role.isSuperAdmin() ? (
                        <FormikCustomerGroupSelect
                            name="customerGroupId"
                            label={translate('customerGroupSelect.label')}
                            disabled={isSubmitting}
                            markAsRequired
                        />
                    ) : null}

                    <FormikPortDetailSelect name="portId" schedule={[]} disabled={isSubmitting} markAsRequired />

                    <FormVesselLimitationsSection
                        vessels={values.vessels}
                        onSubmit={(vessels) => formik.setFieldValue('vessels', vessels)}
                        disabled={isSubmitting}
                        scope={values.vesselScope}
                        onScopeChange={(scope) => formik.setFieldValue('vesselScope', scope)}
                        groupId={values.customerGroupId}
                    />

                    <FormikDebug />
                </DrawerContentLayout>
            </Form>
        </FormikProvider>
    );
};
